import { useState, useEffect, useCallback, useRef } from 'react';
import type { AssetAnalysisData } from '../types';

interface UseAssetAnalysisDataParams {
  open: boolean;
  followUserId?: string;
  fetchData?: (followUserId?: string) => Promise<AssetAnalysisData[]>;
}

interface UseAssetAnalysisDataReturn {
  analysisData: AssetAnalysisData[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * 管理资产分析数据的获取和状态
 */
export function useAssetAnalysisData({
  open,
  followUserId,
  fetchData
}: UseAssetAnalysisDataParams): UseAssetAnalysisDataReturn {
  const [analysisData, setAnalysisData] = useState<AssetAnalysisData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const fetchDataRef = useRef(fetchData);

  // 更新 ref，避免依赖项问题
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  // 默认的数据获取函数（模拟数据）
  const defaultFetchData = async (): Promise<AssetAnalysisData[]> => {
    // 模拟API调用延迟
    await new Promise(resolve => setTimeout(resolve, 1000));

    return [
      {
        id: '1',
        totalAssets: 8560000,
        totalProfitLoss: 319200,
        totalReturnRate: 3.73,
        assetAllocation: {
          equity: 45,
          fixedIncome: 30,
          cash: 15,
          commodity: 10
        },
        topPerformers: [
          {
            id: '1',
            name: '汇添富消费行业混合',
            code: '000083',
            profit: 125000,
            returnRate: 20,
            rank: 1
          },
          {
            id: '2',
            name: '易方达蓝筹精选混合',
            code: '005827',
            profit: 98000,
            returnRate: 15.6,
            rank: 2
          },
          {
            id: '3',
            name: '华夏沪深300ETF',
            code: '000051',
            profit: 75000,
            returnRate: 12.3,
            rank: 3
          }
        ],
        createTime: '2024-01-15 10:30:00'
      },
      {
        id: '2',
        totalAssets: 9200000,
        totalProfitLoss: 450000,
        totalReturnRate: 5.12,
        assetAllocation: {
          equity: 50,
          fixedIncome: 25,
          cash: 20,
          commodity: 5
        },
        topPerformers: [
          {
            id: '4',
            name: '招商中证白酒指数',
            code: '161725',
            profit: 180000,
            returnRate: 25.8,
            rank: 1
          },
          {
            id: '5',
            name: '广发小盘成长混合',
            code: '162703',
            profit: 120000,
            returnRate: 18.2,
            rank: 2
          },
          {
            id: '6',
            name: '南方中证500ETF',
            code: '510500',
            profit: 95000,
            returnRate: 14.5,
            rank: 3
          }
        ],
        createTime: '2024-01-10 14:20:00'
      }
    ];
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await (fetchDataRef.current || defaultFetchData)(followUserId);
      setAnalysisData(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('获取资产分析数据失败');
      setError(error);
      console.error('获取资产分析数据失败:', error);
    } finally {
      setLoading(false);
    }
  }, [followUserId]);

  // 当对话框打开时加载数据
  useEffect(() => {
    if (open) {
      loadData();
    } else {
      // 对话框关闭时清空数据
      setAnalysisData([]);
      setError(null);
    }
  }, [open, loadData]);

  const refresh = useCallback(async () => {
    await loadData();
  }, [loadData]);

  return {
    analysisData,
    loading,
    error,
    refresh
  };
}
